import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { CssBaseline } from '@mui/material';
import { ColorModeProvider } from './contexts/ColorModeContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Login } from './components/Login';
import { Layout } from './components/Layout';
import { Dashboard } from './pages/Dashboard';
import { CreatePost } from './pages/CreatePost';
import { PostHistory } from './pages/PostHistory';
import { IdeaStaging } from './pages/IdeaStaging';
import { ApiDocs } from './pages/ApiDocs';
import { Users } from './pages/Users';
import { Profile } from './pages/Profile';
import { PlatformConnections } from './pages/PlatformConnections';
import { Analytics } from './pages/Analytics';
import { Settings } from './pages/Settings';
import { Calendar } from './pages/Calendar';
import { PostingSchedule } from './pages/PostingSchedule';
import { Guide } from './pages/Guide';
import { Changelog } from './pages/Changelog';
import { ActivityLog } from './pages/ActivityLog';
import { MarketingTools } from './pages/MarketingTools';
import { Recordings } from './pages/Recordings';
import { About } from './pages/About';
import { Privacy } from './pages/Privacy';
import { Terms } from './pages/Terms';

// Protected route wrapper
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

// Public route wrapper (redirect to dashboard if already logged in)
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

const AppRoutes: React.FC = () => {
  return (
    <Routes>
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      {/* One frame for every signed-in page. Each page used to wrap itself in its own <Layout>,
          so every menu click tore down and rebuilt the sidebar, top bar and banners — refetching
          workspaces and connections and forgetting a collapsed sidebar — before the page could
          load (Ian noticed the hesitation, 2026-10-17). Now the frame mounts once and only the
          page inside it (the Outlet) changes. */}
      <Route
        element={
          <ProtectedRoute>
            <Layout>
              <Outlet />
            </Layout>
          </ProtectedRoute>
        }
      >
        <Route path="/" element={<Dashboard />} />
        <Route path="/create" element={<CreatePost />} />
        <Route path="/guide" element={<Guide />} />
        <Route path="/schedule" element={<PostingSchedule />} />
        <Route path="/ideas" element={<IdeaStaging />} />
        <Route path="/history" element={<PostHistory />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/users" element={<Users />} />
        <Route path="/changelog" element={<Changelog />} />
        <Route path="/marketing-tools" element={<MarketingTools />} />
        <Route path="/recordings" element={<Recordings />} />
        <Route path="/activity" element={<ActivityLog />} />
        <Route path="/api-documentation" element={<ApiDocs />} />
        <Route path="/connections" element={<PlatformConnections />} />
        <Route path="/analytics" element={<Analytics />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/calendar" element={<Calendar />} />
      </Route>
      {/* The Scheduled Posts page was a stub that always claimed there was nothing
          scheduled. The calendar now shows the real queue, so send bookmarks there. */}
      <Route
        path="/scheduled"
        element={<Navigate to="/calendar" replace />}
      />
      {/* Public, like Privacy and Terms: it explains the app to someone who has no account */}
      <Route path="/about" element={<About />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
    </Routes>
  );
};

function App() {
  return (
    // Light, dark or match the computer — see ColorModeContext and the toggle in the top bar
    <ColorModeProvider>
      <CssBaseline />
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </BrowserRouter>
    </ColorModeProvider>
  );
}

export default App;
